import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Long lists. A table with thousands of rows is not drawn completely: only the rows in view (plus a few more) exist
 * in the page, the rest is a spacer of the right height, so scrolling stays smooth with ten thousand rows and the
 * list scrolls inside its own frame (the header row stays visible). Rows have a fixed height: long texts are cut with
 * "…" and complete in the tooltip or in the detail view.
 */

interface Props<T> {
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** height of one row in pixels (all rows are equally high) */
  rowHeight: number;
  /** header row(s) of the table: <tr> elements; stays on top while scrolling */
  head: ReactNode;
  /** the <td> cells of a row */
  renderRow: (row: T, index: number) => ReactNode;
  /** attributes of the <tr> of a row (click handler, highlight) */
  rowProps?: (row: T, index: number) => HTMLAttributes<HTMLTableRowElement>;
  /** frame height: shorter lists get exactly their height; longer ones scroll */
  maxHeight?: string;
  minWidth?: number;
  /** equal-height columns with the widths set on the header cells (w-40 …); texts are cut with "…" */
  fixed?: boolean;
  className?: string;
  /** called when the person scrolls near the end (to load further pages from the server) */
  onNearEnd?: () => void;
  /** index of a row to bring into view when it changes (keyboard navigation) */
  scrollToIndex?: number | null;
  /** the filters of the list: when they change the list starts at the top again (a longer list of one filter must not leave the view far down in the short list of the next) */
  resetKey?: string;
  label?: string;
}

const OVERSCAN = 8;

export function VirtualTable<T>({ rows, rowKey, rowHeight, head, renderRow, rowProps, maxHeight = 'min(68vh, 44rem)', minWidth, fixed, className, onNearEnd, scrollToIndex, resetKey, label }: Props<T>) {
  const box = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  const [top, setTop] = useState(0);
  const [view, setView] = useState(600);
  const raf = useRef(0);

  const measure = useCallback(() => {
    const el = box.current;
    if (!el) return;
    setView(el.clientHeight);
  }, []);
  useLayoutEffect(measure, [measure, rows.length]);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const onScroll = () => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const el = box.current;
      if (!el) return;
      setTop(el.scrollTop);
      if (onNearEnd && el.scrollHeight - el.scrollTop - el.clientHeight < rowHeight * 20) onNearEnd();
    });
  };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  useEffect(() => {
    if (scrollToIndex === null || scrollToIndex === undefined || !box.current) return;
    const el = box.current;
    const headH = headRef.current?.offsetHeight ?? 0;
    const y = scrollToIndex * rowHeight;
    if (y < el.scrollTop) el.scrollTop = y;
    else if (y + rowHeight > el.scrollTop + el.clientHeight - headH) el.scrollTop = y + rowHeight - el.clientHeight + headH;
  }, [scrollToIndex, rowHeight]);

  // new filters: back to the top (not on the first drawing)
  const lastReset = useRef(resetKey);
  useLayoutEffect(() => {
    if (lastReset.current === resetKey) return;
    lastReset.current = resetKey;
    if (box.current) box.current.scrollTop = 0;
    setTop(0);
  }, [resetKey]);

  // The scroll position of the last scroll event can lie behind the end of a list that became shorter (new filter): the
  // rows are cut from the position the browser will move to, not from the old one (otherwise the frame unwinds frame by frame)
  const topNow = Math.min(top, Math.max(0, rows.length * rowHeight - view));
  const first = Math.max(0, Math.floor(topNow / rowHeight) - OVERSCAN);
  const last = Math.min(rows.length, Math.ceil((topNow + view) / rowHeight) + OVERSCAN);
  const slice = rows.slice(first, last);
  return (
    <div
      ref={box}
      onScroll={onScroll}
      role="region"
      aria-label={label}
      tabIndex={0}
      className={clsx('overflow-auto rounded-md border border-line bg-sheet [overflow-anchor:none] [scrollbar-width:thin]', className)}
      style={{ maxHeight }}
    >
      <table className={clsx('w-full border-collapse text-sm', fixed && 'table-fixed')} style={{ minWidth }}>
        <thead ref={headRef} className="sticky top-0 z-10 border-b border-line bg-sheet-2">
          {head}
        </thead>
        <tbody>
          {first > 0 && (
            <tr aria-hidden style={{ height: first * rowHeight }}>
              <td />
            </tr>
          )}
          {slice.map((row, i) => {
            const extra = rowProps?.(row, first + i);
            return (
              <tr key={rowKey(row, first + i)} {...extra} style={{ height: rowHeight }} className={clsx('border-b border-line/60 last:border-0', extra?.className)}>
                {renderRow(row, first + i)}
              </tr>
            );
          })}
          {last < rows.length && (
            <tr aria-hidden style={{ height: (rows.length - last) * rowHeight }}>
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Standard cell of a fixed-height row: one or two lines, cut with "…". */
export const cell = 'px-3 py-1 align-middle overflow-hidden';
export const clip = 'block truncate';

/**
 * Frame for a list that is not a table (cards, rows): scrolls inside, with a sensible height. On paper the frame
 * has no limit (print css).
 */
export function ScrollBox({ children, maxHeight = 'min(60vh, 34rem)', className, label }: { children: ReactNode; maxHeight?: string; className?: string; label?: string }) {
  return (
    <div role="region" aria-label={label} tabIndex={0} className={clsx('scroll-box overflow-y-auto [scrollbar-width:thin]', className)} style={{ maxHeight }}>
      {children}
    </div>
  );
}

/**
 * "Load more while scrolling" for lists that are drawn in portions: calls `onMore` when the end of the list comes
 * near. Place <Sentinel/> after the last row inside the scrolling frame; it overlaps the last rows (no extra space).
 */
export function Sentinel({ onMore, disabled }: { onMore: () => void; disabled?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || disabled || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && onMore(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [onMore, disabled]);
  return <div ref={ref} aria-hidden className="pointer-events-none relative -mt-48 h-48" />;
}

/**
 * A long list that is not loaded in pages from the server is still drawn in portions: the first `size` rows, then
 * more while the person scrolls. `resetKey` (the filters) starts again at the top. The number of rows in the list
 * and the filters stay complete: only the drawing is portioned.
 */
export function useChunked<T>(rows: T[], size = 100, resetKey: unknown = '') {
  const [count, setCount] = useState(size);
  const key = typeof resetKey === 'string' ? resetKey : JSON.stringify(resetKey);
  useEffect(() => setCount(size), [key, size]);
  const more = useCallback(() => setCount((c) => c + size), [size]);
  const visible = useMemo(() => (rows.length > count ? rows.slice(0, count) : rows), [rows, count]);
  return { visible, hasMore: rows.length > count, more, shown: Math.min(count, rows.length), total: rows.length };
}

/**
 * Frame for an ordinary <table> with many rows: the table scrolls inside the frame, the header row (and a total row
 * in <tfoot>) stays visible. Combine with useChunked + <Sentinel/> for thousands of rows. `bare`: no border of its own
 * (inside a panel).
 */
export function ScrollTable({ children, maxHeight = 'min(72vh, 52rem)', className, label, bare }: { children: ReactNode; maxHeight?: string; className?: string; label?: string; bare?: boolean }) {
  return (
    <div role="region" aria-label={label} tabIndex={0} className={clsx('scroll-box scroll-table overflow-auto [scrollbar-width:thin]', !bare && 'rounded-lg border border-line bg-sheet', className)} style={{ maxHeight }}>
      {children}
    </div>
  );
}
